export const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

export const formatDelta = (num: number): string => {
  const sign = num >= 0 ? '+' : '';
  return `${sign}${formatNumber(num)}`;
};

export const formatDeltaClass = (num: number): string => {
  if (num > 0) return 'text-green-400';
  if (num < 0) return 'text-red-400';
  return 'text-gray-400';
};

/**
 * Formats a per-acre metric (OPA, DPA, TPA, WPA) to 1 decimal place.
 * Guards against division by zero.
 *
 * @param value The raw value (e.g., offense points)
 * @param acres The total acres
 * @returns Formatted string to 1 decimal place (e.g., "3.4")
 */
/** Formats an UPPER_SNAKE_CASE enum value to Title Case (e.g., "NO_RESEARCH" → "No Research"). */
export const formatEnumLabel = (value: string): string =>
  value.toLowerCase().replace(/(^|_)(\w)/g, (_, prefix, char) =>
    (prefix ? ' ' : '') + char.toUpperCase()
  );

export const formatPerAcre = (value: number, acres: number): string => {
  if (acres <= 0) return '0.0';
  return (value / acres).toFixed(1);
};

/**
 * Formats networth-per-acre as a rounded whole number (e.g. 205377 nw / 200 ac -> "1,027"),
 * distinct from `formatPerAcre` (OPA/DPA/TPA/WPA), which keeps 1-decimal precision because that
 * digit matters there. Guards against division by zero.
 */
export const formatNetworthPerAcre = (networth: number, acres: number): string => {
  return acres > 0 ? formatNumber(Math.round(networth / acres)) : '0';
};

/**
 * Formats a modifier multiplier as a percentage (e.g., 1.021 → "+2.1%", 0.85 → "−15.0%").
 * Used in Council page modifier displays.
 */
export const modPct = (multiplier: number): string => {
  const pct = (multiplier - 1) * 100;
  const sign = pct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
};

/**
 * Formats a raw percentage value (e.g., 0.25 → "25.0%", 0.5 → "50.0%").
 */
export const pct = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * Formats a number to 1 decimal place (e.g., 123.456 → "123.5").
 */
export const fmtDec = (value: number): string => {
  return value.toFixed(1);
};

/**
 * Abbreviates a number at >=100,000 (e.g. 186,204 -> "186k", 1,034,812 -> "1.03M"); below that
 * threshold returns the exact `formatNumber` value unchanged. SF-168: narrowly scoped to the
 * mobile ResourceBar pill's Networth display and its alert chip's compact value — every other
 * Networth display in the app keeps the exact `formatNumber`.
 */
export const formatCompactNumber = (value: number): string => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs < 100_000) return formatNumber(value);
  const kValue = Math.round(abs / 1_000);
  if (kValue < 1000) return `${sign}${kValue}k`;
  return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
};
